import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'libs/styled';
import { ProductCard, type ProductCardProduct } from 'components/product/ProductCard';
import { columnWidthPx, imageHeightFromSpanWidthPx, spanWidthPx } from 'Utils/gridLayoutPx';
import { isWideProduct } from 'Utils/isWideProduct';
import type { ContentSliderDesktopApi } from 'components/sliders/ContentSlider/types';
import { GRID_GAP_PX } from 'config/branding/grid';

/**
 * ProductSliderCarousel — desktop product carousel with one wider “active” card and narrower neighbours on a 24-col grid.
 *
 * The desktop body picks this implementation when the list does not fit `ProductSliderStaticGrid`.
 *
 * Layout: `computeLayout` assigns each product a signed **slot** around `activeIndex` (`circularSlot`) and pixel widths
 * from `spansForProduct` (narrow 4/6 or wide 8/12). The strip is translated so the active item is viewport-centred.
 * `displayX[i]` is each slide’s **left edge**; `SlideSlot` positions by **centre** (`left` + `translateX(-50%)`).
 *
 * Navigation: `activeIndex` ±1. Non-wrapping slides animate via Emotion (`$isAnimating`). **Teleporting** items (slot jump > 1)
 * use imperative inline `left`/`width` in `useLayoutEffect`. **Ghosts** are temporary duplicate slides so exits/entries cross-fade.
 * **travelCenterPx** matches the largest non-teleport centre move (with `fallbackStepPx` floor) so motion speeds stay consistent.
 *
 * **Timing:** `NAV_ANIMATION_MS` clears `isAnimating` and ghosts shortly after the CSS transition length.
 *
 * **Parent:** `onReady` wires `SliderHeader` chevrons (`scrollPrev` / `scrollNext`, enable flags).
 */
const GRID_COLS = 24;

/** Grid span count for inactive **narrow** cards (image height, `spansForProduct`, fallback step math). */
const NARROW_INACTIVE_SPAN_COLS = 4;

/** Grid span count for active **narrow** cards. */
const NARROW_ACTIVE_SPAN_COLS = 6;

/** Grid span count for inactive **wide** cards. */
const WIDE_INACTIVE_SPAN_COLS = 8;

/** Grid span count for active **wide** cards. */
const WIDE_ACTIVE_SPAN_COLS = 12;

/**
 * Vertical space below the image reserved for title/designer text so the track height matches visible cards.
 * Mirrors `ProductScrollMobile` assumptions (line-clamp × line-height + margin).
 */
const PRODUCT_CARD_INFO_RESERVE_PX = Math.ceil(3 * 15 * 1.2) + 7;

/**
 * Duration in ms for JS timers only (`NAV_ANIMATION_MS`). Must match `--transition-slider-duration` in `styles.css` (0.5s → 500).
 * CSS `transition` strings use `var(--transition-slider)` in `SlideSlot` and in teleport `el.style` (imperative reflow, not styled-only).
 */
const SLIDER_TRANSITION_MS = 500;

/**
 * How long `isAnimating` stays true after a nav click. Slightly longer than `SLIDER_TRANSITION_MS` so the browser can
 * paint the final frame before we strip inline styles and remove ghosts.
 */
const NAV_ANIMATION_MS = SLIDER_TRANSITION_MS + 50;

const SLIDER_MOTION_TRANSITION_LEFT_ONLY = 'left var(--transition-slider)';
const SLIDER_MOTION_TRANSITION_LEFT_AND_WIDTH = 'left var(--transition-slider), width var(--transition-slider)';

const filterTransientProps = { shouldForwardProp: (prop: string) => !prop.startsWith('$') };

/** Breaks out of the parent grid padding so slides can bleed edge-to-edge horizontally. */
const SliderBleedWrapper = styled('div')`
    grid-column: 1 / -1;
    margin-left: calc(-1 * var(--grid-padding, 10px));
    margin-right: calc(-1 * var(--grid-padding, 10px));
    width: calc(100% + 2 * var(--grid-padding, 10px));
`;

/** Clips the carousel; receives swipe handlers. `touch-action` keeps vertical page scroll usable. */
const Viewport = styled('div')`
    position: relative;
    width: 100%;
    overflow: hidden;
    touch-action: pan-y pinch-zoom;
`;

/** Absolutely positioned slides stack here; explicit `height` is set from JS because children do not stretch the flow. */
const Track = styled('div')`
    position: relative;
    width: 100%;
`;

/**
 * Converts layout **left edge** (`displayX`) + width to the **centre X** used as CSS `left` on `SlideSlot`
 * (because the slot also uses `translateX(-50%)`).
 */
const centerXFromLeftEdge = (leftEdgePx: number, widthPx: number): number => leftEdgePx + widthPx / 2;

/** Formats a number as `px` for inline styles during teleport (stable rounding to 2 decimals). */
const slideInlinePx = (value: number): string => `${Math.round(value * 100) / 100}px`;

const SlideSlot = styled('div', filterTransientProps)<{
    $centerX: number;
    $width: number;
    $isAnimating?: boolean;
}>`
    position: absolute;
    top: 0;
    left: ${({ $centerX }) => $centerX}px;
    transform: translateX(-50%);
    width: ${({ $width }) => $width}px;
    overflow: hidden;
    min-width: 0;
    will-change: left, width;

    transition: ${({ $isAnimating }) => ($isAnimating ? SLIDER_MOTION_TRANSITION_LEFT_AND_WIDTH : 'none')};
`;

/** Wraps `ProductCard`; sets `container-type` and `--product-image-height` for responsive image ratio. */
const SlideInner = styled('div', filterTransientProps)<{ $imageHeightPx: number | null }>`
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    container-type: inline-size;
    --product-image-height: ${({ $imageHeightPx }) =>
        $imageHeightPx != null ? `${$imageHeightPx}px` : 'calc(100cqw * 5 / 4)'};
`;

const spansForProduct = (product: ProductCardProduct): { inactive: number; active: number } => {
    const wide = isWideProduct(product);
    return wide
        ? { inactive: WIDE_INACTIVE_SPAN_COLS, active: WIDE_ACTIVE_SPAN_COLS }
        : { inactive: NARROW_INACTIVE_SPAN_COLS, active: NARROW_ACTIVE_SPAN_COLS };
};

const slideWidthForIndex = (
    products: ProductCardProduct[],
    index: number,
    activeIndex: number,
    columnWidth: number,
    gapPx: number,
): number => {
    const product = products[index];
    const { inactive, active } = spansForProduct(product);
    const span = index === activeIndex ? active : inactive;
    return spanWidthPx(span, columnWidth, gapPx);
};

/**
 * Signed slot index for `itemIndex` relative to `activeIdx` on a circle (shortest path around the list).
 * Example: for 7 items, slots roughly range from `-3` … `3` with `0` = active.
 */
const circularSlot = (itemIndex: number, activeIdx: number, numberOfItems: number): number => {
    let diff = (((itemIndex - activeIdx) % numberOfItems) + numberOfItems) % numberOfItems;
    if (diff >= Math.ceil(numberOfItems / 2)) diff -= numberOfItems;
    return diff;
};

/**
 * True if this product’s slot jumps by more than one when going from `prevActiveIdx` → `activeIdx`
 * (wrap-around). Those slides need the inline teleport animation + usually a ghost.
 */
const isTeleporting = (itemIndex: number, activeIdx: number, prevActiveIdx: number, numberOfItems: number): boolean => {
    if (numberOfItems <= 1 || activeIdx === prevActiveIdx) return false;
    const prevSlot = circularSlot(itemIndex, prevActiveIdx, numberOfItems);
    const currSlot = circularSlot(itemIndex, activeIdx, numberOfItems);
    return Math.abs(currSlot - prevSlot) > 1;
};

/** Output of `computeLayout`: per-index left edges, widths, and resolved column width. */
interface LayoutResult {
    /** Left edge X in track coordinates (before centre-based CSS). */
    displayX: number[];
    /** Pixel width of each slide, same index order as `products`. */
    slideWidths: number[];
    /** Single grid column width in px (from `columnWidthPx`). */
    columnWidth: number;
}

/**
 * Builds the horizontal strip for the current `activeIndex`: ordered slots, gaps, then centres the active item in `viewportWidth`.
 * Returns `null` if inputs are invalid (no products, zero width, etc.).
 *
 * @param products - Product list; order matches slide indices.
 * @param activeIndex - Which item is centred in the viewport; drives inactive vs active span widths.
 * @param viewportWidth - Visible track width in px (carousel viewport).
 * @param gapPx - Horizontal gap between slides in px (same token as grid gap).
 * @param gridCols - Column count for the underlying grid (e.g. 24) when deriving `columnWidth`.
 */
const computeLayout = (
    products: ProductCardProduct[],
    activeIndex: number,
    viewportWidth: number,
    gapPx: number,
    gridCols: number,
): LayoutResult | null => {
    const numberOfProducts = products.length;
    if (numberOfProducts === 0 || viewportWidth <= 0) {
        return null;
    }

    const columnWidth = columnWidthPx(viewportWidth, gapPx, gridCols);
    if (columnWidth <= 0) {
        return null;
    }

    const slideWidths: number[] = new Array(numberOfProducts);
    const circularSlots: number[] = new Array(numberOfProducts);
    for (let productIndex = 0; productIndex < numberOfProducts; productIndex += 1) {
        slideWidths[productIndex] = slideWidthForIndex(products, productIndex, activeIndex, columnWidth, gapPx);
        circularSlots[productIndex] = circularSlot(productIndex, activeIndex, numberOfProducts);
    }

    const productIndicesInSlotOrder = [...Array(numberOfProducts).keys()].sort((a, b) => {
        if (circularSlots[a] !== circularSlots[b]) return circularSlots[a] - circularSlots[b];
        return a - b;
    });

    const leftEdgeBeforeViewportCenteringPx: number[] = new Array(numberOfProducts);
    leftEdgeBeforeViewportCenteringPx[productIndicesInSlotOrder[0]] = 0;
    for (let orderIndex = 1; orderIndex < numberOfProducts; orderIndex += 1) {
        const currentProductIndex = productIndicesInSlotOrder[orderIndex];
        const previousProductIndex = productIndicesInSlotOrder[orderIndex - 1];
        leftEdgeBeforeViewportCenteringPx[currentProductIndex] =
            leftEdgeBeforeViewportCenteringPx[previousProductIndex] + slideWidths[previousProductIndex] + gapPx;
    }

    const activeSlideWidth = slideWidths[activeIndex];
    const activeLeftBeforeCentering = leftEdgeBeforeViewportCenteringPx[activeIndex];
    const translateX = viewportWidth / 2 - (activeLeftBeforeCentering + activeSlideWidth / 2);

    const displayX: number[] = new Array(numberOfProducts);
    for (let productIndex = 0; productIndex < numberOfProducts; productIndex += 1) {
        displayX[productIndex] = translateX + leftEdgeBeforeViewportCenteringPx[productIndex];
    }

    return { displayX, slideWidths, columnWidth };
};

/**
 * Largest absolute movement of the **centre** among slides that do *not* teleport between the two layouts.
 * Teleport and ghost animations use this (with a minimum `fallbackStepPx`) so their 500ms motion matches everyone else.
 *
 * @param products - Same list as for `computeLayout`; used for length and per-index slide identity.
 * @param prevActiveIdx - Active index **before** the navigation (wrap / multi-slot jump).
 * @param activeIdx - Active index **after** the navigation.
 * @param oldLayout - Layout from `computeLayout` at `prevActiveIdx`.
 * @param newLayout - Layout from `computeLayout` at `activeIdx`.
 */
const maxNonTeleportingCenterDeltaPx = (
    products: ProductCardProduct[],
    prevActiveIdx: number,
    activeIdx: number,
    oldLayout: LayoutResult,
    newLayout: LayoutResult,
): number => {
    const numberOfProducts = products.length;
    let maxD = 0;
    for (let idx = 0; idx < numberOfProducts; idx += 1) {
        if (!isTeleporting(idx, activeIdx, prevActiveIdx, numberOfProducts)) {
            const c0 = centerXFromLeftEdge(oldLayout.displayX[idx], oldLayout.slideWidths[idx]);
            const c1 = centerXFromLeftEdge(newLayout.displayX[idx], newLayout.slideWidths[idx]);
            maxD = Math.max(maxD, Math.abs(c1 - c0));
        }
    }
    return maxD;
};

/** Temporary duplicate of a teleporting product rendered during nav; removed when animation completes. */
interface GhostEntry {
    item: ProductCardProduct;
    itemIndex: number;
}

export interface ProductSliderCarouselProps {
    products: ProductCardProduct[];
    /** Called so the parent header can wire chevrons (`scrollPrev` / `scrollNext`) and disabled states. */
    onReady?: (api: ContentSliderDesktopApi) => void;
}

const ProductSliderCarousel: React.FC<ProductSliderCarouselProps> = ({ products, onReady }) => {
    // --- DOM refs (slide roots for imperative teleport + ghost animation) ---
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const slotElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());
    const ghostElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());

    const [activeIndex, setActiveIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [ghosts, setGhosts] = useState<GhostEntry[]>([]);
    const prevActiveRef = useRef(0);
    const navDirectionRef = useRef<'left' | 'right'>('right');
    const navigationTimeoutRef = useRef<number | null>(null);

    const [viewportWidth, setViewportWidth] = useState(0);
    /**
     * When false, `ProductCard` image height changes apply instantly. Flips true after mount / product-set settle
     * (two rAFs) so the first paint does not “grow” from wrong `cqw` height.
     */
    const [enableImageHeightTransition, setEnableImageHeightTransition] = useState(false);
    /** Detects product list identity changes to reset `activeIndex` to 0. */
    const previousProductIdsKeyRef = useRef<string | null>(null);

    const productIdsKey = useMemo(() => products.map((product) => product.objectID).join(','), [products]);

    // --- Viewport width: sync before paint, then keep in sync on resize ---
    /** Prime `viewportWidth` from layout so the first frame is not laid out at 0 width. */
    useLayoutEffect(() => {
        const node = viewportRef.current;
        if (!node) return;
        const { width } = node.getBoundingClientRect();
        if (width > 0) {
            setViewportWidth((previous) => (previous === width ? previous : width));
        }
    }, [products.length]);

    /** Follow viewport width when the slider container resizes (or `ResizeObserver` is available). */
    useEffect(() => {
        const node = viewportRef.current;
        if (!node || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                setViewportWidth(entry.contentRect.width);
            }
        });
        resizeObserver.observe(node);
        setViewportWidth(node.getBoundingClientRect().width);
        return () => resizeObserver.disconnect();
    }, [products.length]);

    const numberOfItems = products.length;

    const layout = useMemo(() => {
        if (viewportWidth <= 0 || numberOfItems === 0) {
            return null;
        }
        return computeLayout(products, activeIndex, viewportWidth, GRID_GAP_PX, GRID_COLS);
    }, [products, activeIndex, viewportWidth, numberOfItems]);

    const columnWidth = layout?.columnWidth ?? 0;

    /** New product set → disable height transition until re-enabled below. */
    useLayoutEffect(() => {
        setEnableImageHeightTransition(false);
    }, [productIdsKey]);

    const layoutReady = layout != null && columnWidth > 0 && numberOfItems > 0;

    /** After layout is ready, wait two animation frames then allow smooth image height transitions on cards. */
    useLayoutEffect(() => {
        if (!layoutReady) {
            return undefined;
        }

        let canceled = false;
        let secondAnimationFrameId: number | undefined;
        const firstAnimationFrameId = requestAnimationFrame(() => {
            secondAnimationFrameId = requestAnimationFrame(() => {
                if (!canceled) {
                    setEnableImageHeightTransition(true);
                }
            });
        });

        return () => {
            canceled = true;
            cancelAnimationFrame(firstAnimationFrameId);
            if (secondAnimationFrameId !== undefined) {
                cancelAnimationFrame(secondAnimationFrameId);
            }
        };
    }, [layoutReady, productIdsKey, viewportWidth, numberOfItems, columnWidth]);

    /** Reset to first slide when the `products` list identity changes. */
    useEffect(() => {
        const productSetChanged =
            previousProductIdsKeyRef.current !== null && previousProductIdsKeyRef.current !== productIdsKey;
        previousProductIdsKeyRef.current = productIdsKey;

        if (productSetChanged) {
            setActiveIndex(0);
            prevActiveRef.current = 0;
        }
    }, [productIdsKey]);

    const inactiveImageHeightPx =
        columnWidth > 0
            ? imageHeightFromSpanWidthPx(spanWidthPx(NARROW_INACTIVE_SPAN_COLS, columnWidth, GRID_GAP_PX))
            : null;
    const activeImageHeightPx =
        columnWidth > 0
            ? imageHeightFromSpanWidthPx(spanWidthPx(NARROW_ACTIVE_SPAN_COLS, columnWidth, GRID_GAP_PX))
            : null;

    /**
     * Slides are `position: absolute`, so the track needs a fixed height. We base it on **narrow** 4/6 column
     * image heights only—if we used wide (8/12) spans for wide products, a mixed list would make the strip too tall.
     */
    const trackHeightPx = useMemo(() => {
        if (columnWidth <= 0 || products.length === 0) {
            return 0;
        }
        const inactiveH = imageHeightFromSpanWidthPx(spanWidthPx(NARROW_INACTIVE_SPAN_COLS, columnWidth, GRID_GAP_PX));
        const activeH = imageHeightFromSpanWidthPx(spanWidthPx(NARROW_ACTIVE_SPAN_COLS, columnWidth, GRID_GAP_PX));
        return Math.max(inactiveH, activeH) + PRODUCT_CARD_INFO_RESERVE_PX;
    }, [columnWidth, products.length]);

    /** Width of a product’s **inactive** span (used for ghost end state). */
    const inactiveGhostWidth = (product: ProductCardProduct): number => {
        if (columnWidth <= 0) return 200;
        const { inactive } = spansForProduct(product);
        return spanWidthPx(inactive, columnWidth, GRID_GAP_PX);
    };

    /**
     * Advances `activeIndex`, builds ghost list for teleporting indices, arms the nav timeout, and toggles `isAnimating`.
     * Non-teleport slides rely on Emotion; teleport/ghost slides are updated in the layout effect below.
     */
    const handleNavigate = useCallback(
        (direction: 'left' | 'right') => {
            if (isAnimating || numberOfItems <= 1) return;

            navDirectionRef.current = direction;
            prevActiveRef.current = activeIndex;
            const newIndex =
                direction === 'left'
                    ? (activeIndex - 1 + numberOfItems) % numberOfItems
                    : (activeIndex + 1) % numberOfItems;

            const newGhosts: GhostEntry[] = [];
            products.forEach((product, idx) => {
                if (isTeleporting(idx, newIndex, activeIndex, numberOfItems)) {
                    newGhosts.push({
                        item: product,
                        itemIndex: idx,
                    });
                }
            });
            setGhosts(newGhosts);
            setIsAnimating(true);
            setActiveIndex(newIndex);

            if (navigationTimeoutRef.current != null) {
                window.clearTimeout(navigationTimeoutRef.current);
            }
            navigationTimeoutRef.current = window.setTimeout(() => {
                setIsAnimating(false);
                setGhosts([]);
                navigationTimeoutRef.current = null;
            }, NAV_ANIMATION_MS);
        },
        [activeIndex, isAnimating, numberOfItems, products],
    );

    /** Avoid leaking the nav timeout if the component unmounts mid-animation. */
    useEffect(
        () => () => {
            if (navigationTimeoutRef.current != null) {
                clearTimeout(navigationTimeoutRef.current);
            }
        },
        [],
    );

    const scrollPrev = useCallback(() => handleNavigate('left'), [handleNavigate]);
    const scrollNext = useCallback(() => handleNavigate('right'), [handleNavigate]);

    const canGoPrev = numberOfItems > 1;
    const canGoNext = numberOfItems > 1;

    useEffect(() => {
        onReady?.({
            scrollPrev,
            scrollNext,
            canGoPrev,
            canGoNext,
        });
    }, [onReady, scrollPrev, scrollNext, canGoPrev, canGoNext]);

    /**
     * Teleport / ghost animation driver (runs synchronously after React commits DOM updates).
     * 1. If not animating: strip any leftover inline styles from real slides.
     * 2. If this step has no teleport: nothing to do (Emotion handles motion).
     * 3. Otherwise: compute old/new layouts, set start positions with `transition: none`, force layout, then apply
     *    end positions with inline `transition` matching the global slider duration.
     */
    useLayoutEffect(() => {
        const clearSlotInlineStyles = (): void => {
            products.forEach((_, idx) => {
                const slotKey = `slot-${idx}`;
                const el = slotElementsRef.current.get(slotKey);
                if (el) {
                    el.style.transition = '';
                    el.style.left = '';
                    el.style.transform = '';
                    el.style.width = '';
                }
            });
        };

        if (!isAnimating || viewportWidth <= 0 || numberOfItems === 0) {
            clearSlotInlineStyles();
            return undefined;
        }

        const newActive = activeIndex;
        const oldActive = prevActiveRef.current;
        const navIsNext = navDirectionRef.current === 'right';

        const anyTeleport =
            ghosts.length > 0 || products.some((_, idx) => isTeleporting(idx, newActive, oldActive, numberOfItems));

        if (!anyTeleport) {
            return undefined;
        }

        const oldLayout = computeLayout(products, oldActive, viewportWidth, GRID_GAP_PX, GRID_COLS);
        const endLayout = computeLayout(products, newActive, viewportWidth, GRID_GAP_PX, GRID_COLS);
        if (!oldLayout || !endLayout) {
            clearSlotInlineStyles();
            if (navigationTimeoutRef.current != null) {
                window.clearTimeout(navigationTimeoutRef.current);
                navigationTimeoutRef.current = null;
            }
            setIsAnimating(false);
            setGhosts([]);
            return undefined;
        }

        const animations: {
            el: HTMLDivElement;
            startX: number;
            startW: number;
            endX: number;
            endW: number;
        }[] = [];

        const maxCenterDelta = maxNonTeleportingCenterDeltaPx(products, oldActive, newActive, oldLayout, endLayout);
        /** Minimum travel so teleport is never shorter than “one narrow card + gap” (edge case when max delta is tiny). */
        const fallbackStepPx = spanWidthPx(NARROW_INACTIVE_SPAN_COLS, endLayout.columnWidth, GRID_GAP_PX) + GRID_GAP_PX;
        const travelCenterPx = Math.max(maxCenterDelta, fallbackStepPx);

        products.forEach((_, idx) => {
            if (!isTeleporting(idx, newActive, oldActive, numberOfItems)) return;

            const slotKey = `slot-${idx}`;
            const el = slotElementsRef.current.get(slotKey);
            if (!el) return;

            const endLeft = endLayout.displayX[idx];
            const endW = endLayout.slideWidths[idx];
            const endCenter = centerXFromLeftEdge(endLeft, endW);
            const startCenter = navIsNext ? endCenter + travelCenterPx : endCenter - travelCenterPx;
            const startLeft = startCenter - endW / 2;

            animations.push({
                el,
                startX: startLeft,
                startW: endW,
                endX: endLeft,
                endW,
            });
        });

        ghosts.forEach((ghost, ghostIndex) => {
            const refMapKey = `ghost-${ghostIndex}`;
            const el = ghostElementsRef.current.get(refMapKey);
            if (!el) return;

            const wGhost = inactiveGhostWidth(ghost.item);
            const fromX = oldLayout.displayX[ghost.itemIndex];
            const fromW = oldLayout.slideWidths[ghost.itemIndex];
            const fromCenter = centerXFromLeftEdge(fromX, fromW);
            const toCenter = navIsNext ? fromCenter - travelCenterPx : fromCenter + travelCenterPx;
            const toX = toCenter - wGhost / 2;
            const toW = wGhost;

            animations.push({
                el,
                startX: fromX,
                startW: fromW,
                endX: toX,
                endW: toW,
            });
        });

        if (animations.length === 0) {
            if (navigationTimeoutRef.current != null) {
                window.clearTimeout(navigationTimeoutRef.current);
                navigationTimeoutRef.current = null;
            }
            setIsAnimating(false);
            setGhosts([]);
            return undefined;
        }

        animations.forEach(({ el, startX, startW }) => {
            el.style.transition = 'none';
            el.style.left = slideInlinePx(centerXFromLeftEdge(startX, startW));
            el.style.transform = 'translateX(-50%)';
            el.style.width = slideInlinePx(startW);
        });

        /** Force layout so the browser commits “start” geometry before we attach transitions. */
        animations.forEach(({ el }) => {
            el.getBoundingClientRect();
        });

        animations.forEach(({ el, startW, endX, endW }) => {
            const widthIsConstant = Math.abs(startW - endW) < 0.5;
            el.style.transition = widthIsConstant
                ? SLIDER_MOTION_TRANSITION_LEFT_ONLY
                : SLIDER_MOTION_TRANSITION_LEFT_AND_WIDTH;
            el.style.left = slideInlinePx(centerXFromLeftEdge(endX, endW));
            el.style.transform = 'translateX(-50%)';
            el.style.width = slideInlinePx(endW);
        });

        return undefined;
    }, [activeIndex, isAnimating, products, ghosts, viewportWidth, numberOfItems]);

    /** Arrow keys when focus is inside the viewport (accessibility). */
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!viewportRef.current) return;

            const { activeElement } = document;
            if (!activeElement || !viewportRef.current.contains(activeElement)) {
                return;
            }

            if (event.key === 'ArrowLeft' && canGoPrev) {
                handleNavigate('left');
            } else if (event.key === 'ArrowRight' && canGoNext) {
                handleNavigate('right');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [canGoPrev, canGoNext, handleNavigate]);

    // --- Touch swipe (simple horizontal threshold) ---
    const touchStartX = useRef<number | null>(null);
    const touchEndX = useRef<number | null>(null);

    const handleTouchStart = useCallback((event: React.TouchEvent) => {
        touchStartX.current = event.touches[0].clientX;
    }, []);

    const handleTouchMove = useCallback((event: React.TouchEvent) => {
        touchEndX.current = event.touches[0].clientX;
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (touchStartX.current === null || touchEndX.current === null) return;

        const diff = touchStartX.current - touchEndX.current;
        const threshold = 50;

        if (Math.abs(diff) > threshold) {
            if (diff > 0 && canGoNext) {
                handleNavigate('right');
            } else if (diff < 0 && canGoPrev) {
                handleNavigate('left');
            }
        }

        touchStartX.current = null;
        touchEndX.current = null;
    }, [canGoPrev, canGoNext, handleNavigate]);

    if (numberOfItems === 0) {
        return null;
    }

    const trackHeightStyle = trackHeightPx > 0 ? { height: `${trackHeightPx}px` } : undefined;

    const fallbackWidth = 200;
    const renderLayout = layout;
    const oldLayoutForGhosts =
        ghosts.length > 0
            ? computeLayout(products, prevActiveRef.current, viewportWidth, GRID_GAP_PX, GRID_COLS)
            : null;

    // --- Render: real slides (by product index) + optional ghost duplicates on top ---
    return (
        <SliderBleedWrapper>
            <Viewport
                ref={viewportRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <Track style={trackHeightStyle}>
                    {products.map((product, idx) => {
                        // During teleport, this slot uses inline left/width — keep Emotion transitions off.
                        const teleporting = isTeleporting(idx, activeIndex, prevActiveRef.current, numberOfItems);
                        const slotKey = `slot-${idx}`;
                        const reactKey = String(product.objectID || idx);
                        const displayX = renderLayout?.displayX[idx] ?? 0;
                        const widthPx = renderLayout?.slideWidths[idx] ?? fallbackWidth;
                        const centerX = centerXFromLeftEdge(displayX, widthPx);
                        const imageHeightPx = idx === activeIndex ? activeImageHeightPx : inactiveImageHeightPx;

                        return (
                            <SlideSlot
                                key={reactKey}
                                ref={(el: HTMLDivElement | null) => {
                                    if (el) slotElementsRef.current.set(slotKey, el);
                                    else slotElementsRef.current.delete(slotKey);
                                }}
                                $centerX={centerX}
                                $width={widthPx}
                                $isAnimating={isAnimating && !teleporting}
                            >
                                <SlideInner $imageHeightPx={imageHeightPx}>
                                    <ProductCard
                                        product={product}
                                        gridColumn="1 / -1"
                                        transitionImageHeight={enableImageHeightTransition}
                                    />
                                </SlideInner>
                            </SlideSlot>
                        );
                    })}
                    {/* Ghosts: extra SlideSlots for teleporting products; removed when `setGhosts([])` runs after nav. */}
                    {ghosts.map((ghost, ghostIndex) => {
                        const fromX = oldLayoutForGhosts?.displayX[ghost.itemIndex] ?? 0;
                        const fromW = oldLayoutForGhosts?.slideWidths[ghost.itemIndex] ?? fallbackWidth;
                        const fromCenterX = centerXFromLeftEdge(fromX, fromW);
                        const refMapKey = `ghost-${ghostIndex}`;
                        const imageHeightPx =
                            ghost.itemIndex === prevActiveRef.current ? activeImageHeightPx : inactiveImageHeightPx;

                        return (
                            <SlideSlot
                                key={refMapKey}
                                ref={(el: HTMLDivElement | null) => {
                                    if (el) ghostElementsRef.current.set(refMapKey, el);
                                    else ghostElementsRef.current.delete(refMapKey);
                                }}
                                $centerX={fromCenterX}
                                $width={fromW}
                                $isAnimating={false}
                            >
                                <SlideInner $imageHeightPx={imageHeightPx}>
                                    <ProductCard
                                        product={ghost.item}
                                        gridColumn="1 / -1"
                                        transitionImageHeight={enableImageHeightTransition}
                                    />
                                </SlideInner>
                            </SlideSlot>
                        );
                    })}
                </Track>
            </Viewport>
        </SliderBleedWrapper>
    );
};

export default ProductSliderCarousel;
